import { useState } from 'react';
import { Lock } from '@phosphor-icons/react';
import type { WaitlistJoinStatus } from '@app/shared';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { haptic } from '../lib/tma';
import { Logo } from './shell/Logo';
import { Button, Card, Textarea, Toast } from './ui';

export function WaitlistScreen() {
  const t = useT();
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<WaitlistJoinStatus | null>(null);
  const [failed, setFailed] = useState(false);

  async function submit() {
    setBusy(true);
    setFailed(false);
    try {
      const trimmed = note.trim();
      const res = await api.joinWaitlist(trimmed ? { note: trimmed } : {});
      if (res.status === 'has_access') {
        // Let in while the screen was open: reload to get the real app.
        window.location.reload();
        return;
      }
      setStatus(res.status);
      haptic('success');
    } catch {
      setFailed(true);
      haptic('error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen p-6 max-w-lg mx-auto flex flex-col items-center justify-center text-center safe-top safe-bottom">
      <Logo className="mb-6" />
      <Card variant="surface" padding="none" className="w-full p-5">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-accent-soft text-accent-soft-on mb-4">
          <Lock size={28} />
        </div>
        <h1 className="text-xl font-extrabold mb-2">{t.waitlist.title}</h1>

        {status ? (
          <p className="text-sm text-hint">
            {status === 'added' ? t.waitlist.added : t.waitlist.exists}
          </p>
        ) : (
          <>
            <p className="text-sm text-hint mb-5">{t.waitlist.text}</p>
            <label className="block text-left text-xs text-hint mb-1" htmlFor="waitlist-note">
              {t.waitlist.noteLabel}
            </label>
            <Textarea
              id="waitlist-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t.waitlist.notePlaceholder}
              maxLength={500}
              rows={3}
              className="mb-4"
            />
            <Button variant="primary" size="lg" fullWidth loading={busy} onClick={submit}>
              {t.waitlist.submit}
            </Button>
            {failed && (
              <Toast tone="destructive" className="mt-3">
                {t.common.failed}
              </Toast>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
